import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import CountrySelector from '@/components/CountrySelector'
import { Toast } from 'react-vant'
import { APIWeb } from '@/api'
import CurrencySelectPopup from '@/components/CurrencySelectPopup'
import { useCurrencyList } from '@/hooks'
// @ts-ignore
import countryList from 'react-select-country-list'

// 表单数据类型
interface PayeeForm {
    country: string          // 银行所在国家或地区
    currency: string         // 账户币种
    paymentMethod: string    // 付款方式
    bankName: string         // 银行名称
    swiftCode: string        // SWIFT Code
    accountNumber: string    // 银行账户
    accountName: string      // 账户名
    bankAddress: string      // 收款银行所在地
    payeeAddress: string     // 收款人地址（暂不使用）
    remark: string           // 备注
}

export default React.memo(function AddPayee() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { currencyList } = useCurrencyList()

    // Popup 控制状态
    const [showCurrencyPopup, setShowCurrencyPopup] = useState(false)

    // 国家列表选项
    const countryOptions = useMemo(() => {
        const countries = countryList().getData()
        return countries.map((country: any) => ({
            value: country.value,
            label: country.label
        }))
    }, [])

    // 表单数据
    const [formData, setFormData] = useState<PayeeForm>({
        country: '',
        currency: '',
        paymentMethod: 'SWIFT/BIC',
        bankName: '',
        swiftCode: '',
        accountNumber: '',
        accountName: '',
        bankAddress: '',
        payeeAddress: '',
        remark: ''
    })

    // 表单校验错误
    const [errors, setErrors] = useState<Partial<Record<keyof PayeeForm, string>>>({})
    
    // 提交加载状态
    const [loading, setLoading] = useState(false)

    // 更新表单字段
    const updateField = useCallback((field: keyof PayeeForm, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        // 清除该字段的错误
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }))
        }
    }, [errors])

    // 选择国家
    const handleCountryChange = useCallback((value: string) => {
        updateField('country', value)
    }, [updateField])

    // 选择币种
    const handleSelectCurrency = useCallback(() => {
        setShowCurrencyPopup(true)
    }, [])

    // 处理币种选择
    const handleCurrencySelect = useCallback((currency: { symbol: string }) => {
        updateField('currency', currency.symbol)
    }, [updateField])

    // 选择付款方式
    const handleSelectPaymentMethod = useCallback(() => {
        // Toast.info(t('即将上线'))
        return false
    }, [t])

    // 表单校验
    const validateForm = useCallback((): boolean => {
        const newErrors: Partial<Record<keyof PayeeForm, string>> = {}

        if (!formData.country) newErrors.country = t('请选择银行所在国家或地区')
        if (!formData.currency) newErrors.currency = t('请选择账户币种')
        if (!formData.bankName) newErrors.bankName = t('请输入银行名称')
        if (!formData.swiftCode) newErrors.swiftCode = t('请选择SWIFTCode')
        if (!formData.accountNumber) newErrors.accountNumber = t('请输入银行账户')
        if (!formData.accountName) newErrors.accountName = t('请输入账户名')
        if (!formData.bankAddress) newErrors.bankAddress = t('请输入收款银行所在地')
        if (!formData.payeeAddress) newErrors.payeeAddress = t('请输入收款人地址')

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }, [formData, t])

    // 检查表单是否填写完整
    const isFormValid = useMemo(() => {
        return (
            formData.country &&
            formData.currency &&
            formData.bankName &&
            formData.swiftCode &&
            formData.accountNumber &&
            formData.accountName &&
            formData.bankAddress &&
            formData.payeeAddress
        )
    }, [formData])

    // 提交表单
    const handleSubmit = useCallback(async () => {
        if (loading) return
        
        if (!validateForm()) {
            Toast.info(t('请完善表单信息'))
            return
        }

        try {
            setLoading(true)
            
            // 构造接口参数
            const params: AddReceivingAccountRequest = {
                account_ccy: formData.currency,           // 账户币种
                account_name: formData.accountName,       // 账户名
                bank_account: formData.accountNumber,     // 银行账户
                bank_address: formData.bankAddress,       // 收款银行所在地
                bank_country: formData.country,           // 银行所在国家或地区
                bank_name: formData.bankName,             // 银行名称（全称）
                payment_method: formData.paymentMethod,   // 付款方式 SWIFT/BIC
                swift_code: formData.swiftCode,           // SWIFT CODE
                remark: formData.remark || undefined,     // 备注（可选）
            }
            
            // 调用添加收款账户接口
            await APIWeb.addReceivingAccount(params)
            
            Toast.success({
                message: t('添加成功'),
                onClose: () => navigate(-1)
            })
        } catch (error) {
            console.error('添加收款账户失败:', error)
            // Toast 会自动显示错误信息
        } finally {
            setLoading(false)
        }
    }, [formData, validateForm, navigate, t, loading])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={
                    <span className="text-3.75 font-medium text-black">{t('添加收款账户')}</span>
                }
            />

            <div className="bg-white px-6 py-6">
                {/* 银行账户详情 */}
                <div className="text-sm font-medium text-black mb-4">{t('银行账户详情')}</div>

                {/* 银行所在国家或地区 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('银行所在国家或地区')}</div>
                    <CountrySelector
                        value={formData.country}
                        onChange={handleCountryChange}
                        placeholder={t('请选择银行所在国家或地区')}
                        options={countryOptions}
                    />
                    {errors.country && <div className="text-xs text-red-500 mt-1">{errors.country}</div>}
                </div>

                {/* 账户币种 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('账户币种')}</div>
                    <div
                        onClick={handleSelectCurrency}
                        className="flex items-center justify-between bg-white border-[0.5px] border-[#EAECF0] border-solid rounded-2.5 px-4 min-h-11.5"
                    >
                        <span className={`text-3 ${formData.currency ? 'text-black' : 'text-[#1A1A1A]/60'}`}>
                            {formData.currency || t('请选择账户币种')}
                        </span>
                        <img src="/icon/select-down.svg" alt="" className="w-3" />
                    </div>
                    {errors.currency && <div className="text-xs text-red-500 mt-1">{errors.currency}</div>}
                </div>

                {/* 付款方式 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('付款方式')}</div>
                    <div
                        onClick={handleSelectPaymentMethod}
                        className="flex items-center justify-between bg-white border-[0.5px] border-[#EAECF0] border-solid rounded-2.5 px-4 min-h-11.5"
                    >
                        <span className={`text-3 ${formData.paymentMethod ? 'text-black' : 'text-[#1A1A1A]/60'}`}>
                            {formData.paymentMethod || t('请选择付款方式')}
                        </span>
                        <img src="/icon/select-down.svg" alt="" className="w-3" />
                    </div>
                </div>

                {/* 银行名称 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('银行名称')} ({t('全称')})</div>
                    <CustomTextInput
                        value={formData.bankName}
                        onChangeText={(text) => updateField('bankName', text)}
                        placeholder={t('例X', { example: 'East West Bank' })}
                        isErrored={!!errors.bankName}
                    />
                    {errors.bankName && <div className="text-xs text-red-500 mt-1">{errors.bankName}</div>}
                </div>

                {/* SWIFT Code */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">SWIFT Code</div>
                    <CustomTextInput
                        value={formData.swiftCode}
                        onChangeText={(text) => updateField('swiftCode', text)}
                        placeholder={t('请选择SWIFTCode')}
                        isErrored={!!errors.swiftCode}
                    />
                    {errors.swiftCode && <div className="text-xs text-red-500 mt-1">{errors.swiftCode}</div>}
                </div>

                {/* 银行账户 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('银行账户')}</div>
                    <CustomTextInput
                        value={formData.accountNumber}
                        onChangeText={(text) => updateField('accountNumber', text)}
                        placeholder={t('例X', { example: '274*****732' })}
                        isErrored={!!errors.accountNumber}
                    />
                    {errors.accountNumber && <div className="text-xs text-red-500 mt-1">{errors.accountNumber}</div>}
                </div>

                {/* 账户名 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('账户名')}</div>
                    <CustomTextInput
                        value={formData.accountName}
                        onChangeText={(text) => updateField('accountName', text)}
                        placeholder={t('例X', { example: 'LEE QUANQUAN' })}
                        isErrored={!!errors.accountName}
                    />
                    {errors.accountName && <div className="text-xs text-red-500 mt-1">{errors.accountName}</div>}
                </div>

                {/* 收款银行所在地 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">
                        {t('收款银行所在地')} ({t('邮编')}/{t('城市')}/{t('州或省')}/{t('国家')})
                    </div>
                    <textarea
                        value={formData.bankAddress}
                        onChange={(e) => updateField('bankAddress', e.target.value)}
                        placeholder=""
                        rows={3}
                        className={`w-full bg-transparent border-0.5 border-solid rounded-2.5 px-4 py-3 text-3 text-black placeholder-[#1A1A1A]/60 outline-none resize-none transition-[border-color,border-width] duration-300 ${errors.bankAddress ? 'border-[#ff0000]' : 'border-[#EAECF0] focus:border-[#191919] focus:border-1'
                            }`}
                    />
                    {errors.bankAddress && <div className="text-xs text-red-500 mt-1">{errors.bankAddress}</div>}
                </div>

                {/* 收款人地址 */}
                <div className="mb-4">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">
                        {t('收款人地址')} ({t('邮编')}/{t('城市')}/{t('州或省')}/{t('国家')})
                    </div>
                    <textarea
                        value={formData.payeeAddress}
                        onChange={(e) => updateField('payeeAddress', e.target.value)}
                        placeholder={t('例X', { example: '1 Infinite loop, Cupertino, CA 95014' })}
                        rows={3}
                        className={`w-full bg-transparent border-0.5 border-solid rounded-2.5 px-4 py-3 text-3 text-black placeholder-[#1A1A1A]/60 outline-none resize-none transition-[border-color,border-width] duration-300 ${errors.payeeAddress ? 'border-[#ff0000]' : 'border-[#EAECF0] focus:border-[#191919] focus:border-1'
                            }`}
                    />
                    {errors.payeeAddress && <div className="text-xs text-red-500 mt-1">{errors.payeeAddress}</div>}
                </div>

                {/* 备注 */}
                <div className="mb-6">
                    <div className="text-xs text-[#0F0F0F]/60 mb-2">{t('备注')}</div>
                    <textarea
                        value={formData.remark}
                        onChange={(e) => updateField('remark', e.target.value)}
                        placeholder={t('最多输入X字符', { maxLength: 50 })}
                        maxLength={50}
                        rows={3}
                        className="w-full bg-transparent border-0.5 border-solid rounded-2.5 px-4 py-3 text-3 text-black placeholder-[#1A1A1A]/60 outline-none resize-none transition-[border-color,border-width] duration-300 border-[#EAECF0] focus:border-[#191919] focus:border-1"
                    />
                </div>

                {/* 提交按钮 */}
                <div className="my-8">
                    <CustomButton 
                        onClick={handleSubmit}
                        disabled={!isFormValid || loading}
                    >
                        {loading ? t('提交中') : t('提交')}
                    </CustomButton>
                </div>
            </div>

            {/* 币种选择 Popup */}
            <CurrencySelectPopup
                visible={showCurrencyPopup}
                onClose={() => setShowCurrencyPopup(false)}
                onSelect={handleCurrencySelect}
                currencies={currencyList}
            />
        </PageLayout>
    )
})
